import { InfoPickup } from "./infoPickup";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { InfoDelivery } from "./infoDelivery";
import { InfoProductsPallet } from "./infoProductsPallet";
import { InfoValidation } from "./infoValidation";
import {
  CustomCotationFormData,
  CustomCotationFormSchema,
} from "@/types/forms/home/customCotationsForm.form";
import { InfoProductsParcel } from "./infoProductsParcel";
import { useNavigate } from "react-router-dom";
import Paths from "@/constants/paths";
import { GetAllCompanyDto, GetOneExpeditionResponseDto } from "@/types/api/api-types";
import { useCreateCustomQuotation, useUpdatePendingCustomQuotation } from "@/api/expedition.api";
import { customQuotationFormDefault } from "./customQuotationFormDefault";
import { GetCustomsV1Response } from "@/types/api/api-types";

type CustomCotationsParcelPalletProps = {
  activeTab: number;
  company: GetAllCompanyDto;
  expedition?: GetOneExpeditionResponseDto;
  customs: GetCustomsV1Response;
};

export const CustomCotationsParcelPallet = ({
  activeTab,
  company,
  expedition,
  customs,
}: CustomCotationsParcelPalletProps) => {
  const navigate = useNavigate();

  const { mutateAsync: updatePendingCustomQuotation, isPending: isUpdatingCustomQuotation } =
    useUpdatePendingCustomQuotation();
  const { mutateAsync: createCustomQuotation, isPending: isCreatingCustomQuotation } =
    useCreateCustomQuotation();

  const isLoading = isUpdatingCustomQuotation || isCreatingCustomQuotation;

  const methods = useForm<CustomCotationFormData>({
    defaultValues: customQuotationFormDefault(activeTab, company, expedition),
    resolver: zodResolver(CustomCotationFormSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const submitForm: SubmitHandler<CustomCotationFormData> = async (data) => {
    const createCustomCotationData = {
      newFilesId: data.newFilesId || [],
      comment: data.comment,
      reference: data.reference,
      type: activeTab === 1 ? "PALETTE" : "PARCEL",
      ...(!data.pickupInfo.manualPickup
        ? {
            pickupLocationId: data.pickupInfo.pickupLocationId,
          }
        : {
            pickupLastName: data.pickupInfo.pickupLastName,
            pickupFirstName: data.pickupInfo.pickupFirstName,
            pickupPhone: data.pickupInfo.pickupPhone,
            pickupCompany: data.pickupInfo.pickupCompany,
            pickupStreet: data.pickupInfo.pickupStreet,
            pickupPostalCode: data.pickupInfo.pickupPostalCode,
            pickupCity: data.pickupInfo.pickupCity,
            pickupCountry: data.pickupInfo.pickupCountry,
          }),
      billingEntityId: data.billingEntityId,
      insurance: data.insurance,
      express: data.express,
      deliveryLastName: data.deliveryInfo.lastName,
      deliveryFirstName: data.deliveryInfo.firstName,
      deliveryPhone: data.deliveryInfo.phone,
      deliveryEmail: data.deliveryInfo.email,
      deliveryStreet: data.deliveryInfo.street,
      deliveryAdditionalInfo: data.deliveryInfo.additionalInfo,
      deliveryAdditionalInfo2: data.deliveryInfo.additionalInfo2,
      deliveryPostalCode: data.deliveryInfo.postalCode,
      deliveryCity: data.deliveryInfo.city,
      customId: data.customId,
      deliveryCompany: data.deliveryInfo.company,
      deliveryStateOrProvinceCode: data.deliveryInfo.stateOrProvinceCode,
      parcelCotation:
        activeTab === 2
          ? {
              parcelCount: data.parcelPackages?.length || 0,
              parcelPackages: data.parcelPackages || [],
              parcelProducts:
                data.parcelProducts?.map((product) => ({
                  type: product.type,
                  description: product.description || null,
                  volume: product.volume || null,
                  bottleQuantity: product.bottleQuantity || null,
                })) || [],
            }
          : null,
      paletteCotation:
        activeTab === 1
          ? {
              palettePackages: data.palettePackages || [],
              paletteProducts: data.paletteProducts || [],
            }
          : null,
    };

    if (expedition) {
      const ret = await updatePendingCustomQuotation({
        id: expedition.id,
        payload: createCustomCotationData,
      });
      if (ret.status === 200 || ret.status === 201) navigate(Paths.home.cotations);
      return;
    }
    try {
      const ret = await createCustomQuotation(createCustomCotationData);
      if (ret.status === 200 || ret.status === 201) {
        if (expedition) {
          navigate(Paths.home.pendingShipments);
        } else {
          navigate(Paths.home.cotations);
        }
      }
    } catch (error) {
      console.error("Error creating custom quotation:", error);
    }
  };

  return (
    <div className="flex flex-1 flex-col gap-6">
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(submitForm)}
          className="flex flex-1 flex-col items-center gap-6"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
            }
          }}
        >
          <InfoPickup company={company} />
          <InfoDelivery customs={customs ?? []} />
          {activeTab === 1 ? <InfoProductsPallet /> : <InfoProductsParcel />}
          <InfoValidation
            documents={expedition?.expeditionDocuments?.documents || []}
            isLoading={isLoading}
            billingEntities={company?.billingEntities || []}
          />
        </form>
      </FormProvider>
    </div>
  );
};
